import partFitsLogo from "../../assets/dashboard/partfits-logo-new-palette.png";
import fitmentArtwork from "../../assets/dashboard/polaris-fitment.png";
import productsArtwork from "../../assets/dashboard/polaris-products.png";
import storefrontArtwork from "../../assets/dashboard/polaris-storefront.png";

const LandingPage = () => {
  return (
    <main className="pf-public-landing">
      <div className="pf-public-landing__glow pf-public-landing__glow--left" aria-hidden="true" />
      <div className="pf-public-landing__glow pf-public-landing__glow--right" aria-hidden="true" />

      <header className="pf-public-landing__header">
        <img className="pf-public-landing__logo" src={partFitsLogo} alt="PartFits" />
        <div className="pf-public-landing__header-actions">
          <nav className="pf-public-landing__nav" aria-label="Public site navigation">
            <a href="#features">Features</a>
            <a href="#workflow">How it works</a>
            <a href="#platform">Platform</a>
          </nav>
          <span className="pf-public-landing__shopify-badge">
            <span className="pf-public-landing__status-dot" aria-hidden="true" />
            Shopify app
          </span>
        </div>
      </header>

      <section className="pf-public-landing__hero" aria-labelledby="pf-public-heading">
        <div className="pf-public-landing__copy">
          <p className="pf-public-landing__eyebrow">Vehicle fitment, made simple</p>
          <h1 id="pf-public-heading">Help shoppers find the parts that fit.</h1>
          <p className="pf-public-landing__intro">
            PartFits gives Shopify merchants one place to manage vehicle compatibility,
            connect products to fitment data, and power a clearer storefront search experience.
          </p>

          <div className="pf-public-landing__access-note">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 10V8a4 4 0 0 1 8 0v2m-9 0h10a1 1 0 0 1 1 1v8H6v-8a1 1 0 0 1 1-1Z" />
            </svg>
            <div>
              <strong>Merchant access</strong>
              <span>Open PartFits from your Shopify admin to continue.</span>
            </div>
          </div>
        </div>

        <div className="pf-public-landing__preview" aria-label="PartFits vehicle search preview">
          <div className="pf-public-landing__preview-bar">
            <div>
              <span className="pf-public-landing__preview-kicker">Storefront preview</span>
              <strong>Find parts for your vehicle</strong>
            </div>
            <span className="pf-public-landing__preview-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M4 15.5V13l2-5h12l2 5v2.5M6.5 15.5h11M7 8l1.5-3h7L17 8M6.5 18v1.5M17.5 18v1.5" />
                <circle cx="7.5" cy="14" r="1" />
                <circle cx="16.5" cy="14" r="1" />
              </svg>
            </span>
          </div>

          <div className="pf-public-landing__selector-grid">
            <div><span>Year</span><strong>2024</strong></div>
            <div><span>Make</span><strong>Ford</strong></div>
            <div><span>Model</span><strong>F-150</strong></div>
          </div>

          <div className="pf-public-landing__result">
            <span className="pf-public-landing__check" aria-hidden="true">&#10003;</span>
            <div>
              <strong>Vehicle selected</strong>
              <span>Showing compatible products</span>
            </div>
            <span className="pf-public-landing__result-count">128 parts</span>
          </div>
        </div>
      </section>

      <section className="pf-public-landing__section pf-public-landing__features" id="features" aria-labelledby="pf-features-heading">
        <div className="pf-public-landing__section-heading">
          <p className="pf-public-landing__eyebrow">Everything in one workflow</p>
          <h2 id="pf-features-heading">From fitment data to a confident purchase.</h2>
          <p>
            Build and maintain the connections between vehicles and products, then bring
            that compatibility experience directly to your storefront.
          </p>
        </div>

        <div className="pf-public-landing__feature-grid">
          <article className="pf-public-landing__feature-card">
            <div className="pf-public-landing__feature-visual pf-public-landing__feature-visual--teal">
              <img src={fitmentArtwork} alt="Vehicle connected to fitment data options" />
            </div>
            <div className="pf-public-landing__feature-body">
              <span className="pf-public-landing__feature-number">01</span>
              <h3>Organize fitment data</h3>
              <p>
                Create a dependable vehicle catalog and keep compatibility records organized
                across year, make, model, and the fields that matter to your catalog.
              </p>
              <span className="pf-public-landing__feature-label">Vehicle catalogs</span>
            </div>
          </article>

          <article className="pf-public-landing__feature-card">
            <div className="pf-public-landing__feature-visual pf-public-landing__feature-visual--gold">
              <img src={productsArtwork} alt="Automotive product package and part" />
            </div>
            <div className="pf-public-landing__feature-body">
              <span className="pf-public-landing__feature-number">02</span>
              <h3>Connect the right products</h3>
              <p>
                Map products and collections to compatible vehicles so your merchandising
                and fitment rules stay connected as the catalog evolves.
              </p>
              <span className="pf-public-landing__feature-label">Product mapping</span>
            </div>
          </article>

          <article className="pf-public-landing__feature-card">
            <div className="pf-public-landing__feature-visual pf-public-landing__feature-visual--mint">
              <img src={storefrontArtwork} alt="Storefront window with search and settings" />
            </div>
            <div className="pf-public-landing__feature-body">
              <span className="pf-public-landing__feature-number">03</span>
              <h3>Guide storefront discovery</h3>
              <p>
                Give shoppers a familiar vehicle selector and help them browse products with
                a clearer understanding of what fits before they buy.
              </p>
              <span className="pf-public-landing__feature-label">Storefront search</span>
            </div>
          </article>
        </div>
      </section>

      <section className="pf-public-landing__workflow" id="workflow" aria-labelledby="pf-workflow-heading">
        <div className="pf-public-landing__workflow-heading">
          <p className="pf-public-landing__eyebrow">A connected process</p>
          <h2 id="pf-workflow-heading">Set up once. Keep the catalog moving.</h2>
          <p>
            PartFits brings the essential pieces of fitment management into one Shopify workflow.
          </p>
        </div>

        <div className="pf-public-landing__steps">
          <article className="pf-public-landing__step">
            <span>1</span>
            <h3>Bring in your data</h3>
            <p>Import or manage the vehicle and compatibility information behind your catalog.</p>
          </article>
          <article className="pf-public-landing__step">
            <span>2</span>
            <h3>Build product connections</h3>
            <p>Review fitments and map the right products or collections to each vehicle.</p>
          </article>
          <article className="pf-public-landing__step">
            <span>3</span>
            <h3>Publish the experience</h3>
            <p>Configure the storefront and let shoppers search with their vehicle in context.</p>
          </article>
        </div>
      </section>

      <section className="pf-public-landing__section pf-public-landing__platform" id="platform" aria-labelledby="pf-platform-heading">
        <div className="pf-public-landing__platform-copy">
          <p className="pf-public-landing__eyebrow">Designed for daily catalog work</p>
          <h2 id="pf-platform-heading">Know what is connected and ready to shop.</h2>
          <p>
            Keep vehicle data, product relationships, imports, and storefront settings close
            together—without losing sight of what needs attention.
          </p>
          <ul>
            <li><span aria-hidden="true">&#10003;</span> Central fitment and vehicle management</li>
            <li><span aria-hidden="true">&#10003;</span> Product and collection-level mapping</li>
            <li><span aria-hidden="true">&#10003;</span> Import history and catalog visibility</li>
            <li><span aria-hidden="true">&#10003;</span> Storefront controls built for Shopify</li>
          </ul>
        </div>

        <div className="pf-public-landing__platform-panel" aria-label="PartFits catalog status preview">
          <div className="pf-public-landing__panel-topbar">
            <div className="pf-public-landing__window-dots" aria-hidden="true"><span /><span /><span /></div>
            <span>Catalog overview</span>
            <span className="pf-public-landing__panel-live"><i /> Live</span>
          </div>
          <div className="pf-public-landing__panel-content">
            <div className="pf-public-landing__panel-title">
              <div>
                <span>PartFits workspace</span>
                <strong>Everything looks connected</strong>
              </div>
              <span className="pf-public-landing__panel-check" aria-hidden="true">&#10003;</span>
            </div>
            <div className="pf-public-landing__status-list">
              <div>
                <span className="pf-public-landing__status-icon pf-public-landing__status-icon--vehicle" aria-hidden="true" />
                <p><strong>Vehicle catalog</strong><span>Fitment data is ready</span></p>
                <b>Ready</b>
              </div>
              <div>
                <span className="pf-public-landing__status-icon pf-public-landing__status-icon--product" aria-hidden="true" />
                <p><strong>Product mappings</strong><span>Connections are up to date</span></p>
                <b>Synced</b>
              </div>
              <div>
                <span className="pf-public-landing__status-icon pf-public-landing__status-icon--store" aria-hidden="true" />
                <p><strong>Storefront experience</strong><span>Vehicle search is available</span></p>
                <b>Live</b>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pf-public-landing__closing" aria-labelledby="pf-closing-heading">
        <div>
          <p className="pf-public-landing__eyebrow">A better fitment experience</p>
          <h2 id="pf-closing-heading">Make compatibility part of the shopping journey.</h2>
          <p>
            PartFits is available to merchants from inside Shopify Admin, where catalog and
            storefront work already happens.
          </p>
        </div>
        <div className="pf-public-landing__closing-access">
          <span className="pf-public-landing__closing-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M8 10V8a4 4 0 0 1 8 0v2m-9 0h10a1 1 0 0 1 1 1v8H6v-8a1 1 0 0 1 1-1Z" />
            </svg>
          </span>
          <p><strong>Already using PartFits?</strong><span>Open the app from your Shopify admin.</span></p>
        </div>
      </section>

      <footer className="pf-public-landing__footer">
        <div className="pf-public-landing__footer-brand">
          <img src={partFitsLogo} alt="PartFits" />
          <span>Vehicle fitment for Shopify stores</span>
        </div>
        <div className="pf-public-landing__footer-contact">
          <span>GoVedia LLC</span>
          <a href="mailto:contact@govedia.com">contact@govedia.com</a>
          <span>&copy; {new Date().getFullYear()} PartFits</span>
        </div>
      </footer>
    </main>
  );
};

export default LandingPage;
